// Espace locataire (/moi/locations/:leaseId) : mes loyers et quittances, mon assurance.
import { ApiError, put } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { Card, Checkbox, Empty, ErrorBox, Loading, Notice, Table } from "@core/ui";
import { InsuranceUpload } from "./bail";
import { Amount, CallStatus, CoverageStatus, DocLink, RECEIPT_STATUS, meApi, useAction } from "./shared";

/** Les sections loyers et assurance concernent le locataire ; un garant n’y a pas accès (réponse « introuvable »). */
function notTenant(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

export function TenantRentSection({ leaseId }: { leaseId: string }) {
  const q = useGet<any>(meApi(leaseId));
  const { busy, run } = useAction("");
  if (q.isLoading) return <Loading />;
  if (notTenant(q.error)) return null;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const b = d.balance;
  return (
    <Card title="Mes loyers et quittances">
      <div className="stats">
        <div className="stat">
          <span className="stat-label">Reste à régler (échu)</span>
          <span className="stat-value">{money(b.overdue_cents)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Prochaines échéances</span>
          <span className="stat-value">{money(b.upcoming_cents)}</span>
        </div>
      </div>
      <p className="small">{d.payment_instructions}</p>
      {d.calls.length === 0 ? (
        <Empty title="Aucune échéance pour le moment" />
      ) : (
        <Table caption="Mes échéances">
          <thead>
            <tr>
              <th>Période</th>
              <th>Échéance</th>
              <th className="right">Montant</th>
              <th className="right">Reste</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {d.calls.slice(0, 12).map((c: any) => (
              <tr key={c.id}>
                <td>{c.period_label}</td>
                <td>{date(c.due_on)}</td>
                <td className="right">
                  <Amount cents={c.total_cents} />
                </td>
                <td className="right">
                  <Amount cents={c.outstanding_cents} />
                </td>
                <td>
                  <CallStatus status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <h3>Quittances et reçus</h3>
      <p className="small muted">Une quittance est délivrée après le règlement intégral d’une période ; un paiement partiel donne lieu à un reçu.</p>
      {d.receipts.length === 0 ? <p className="muted">Aucun document pour le moment.</p> : null}
      <ul className="gestion-list">
        {d.receipts
          .filter((r: any) => r.status !== "annulee")
          .map((r: any) => (
            <li key={r.id} className="gestion-line">
              <span>
                {r.kind === "quittance" ? "Quittance" : "Reçu"} n° {r.number} · {date(r.period_start)} – {date(r.period_end)} · {money(r.amount_cents)}
                <span className="block small muted">{RECEIPT_STATUS[r.status] ?? r.status}</span>
              </span>
              <DocLink id={r.document_id}>Télécharger</DocLink>
            </li>
          ))}
      </ul>
      <Checkbox
        label="Je souhaite recevoir mes quittances par voie électronique"
        help={d.e_receipts_consent ? `Accord enregistré${d.e_receipts_consent_at ? ` le ${dateTime(d.e_receipts_consent_at)}` : ""}. Vous pouvez le retirer à tout moment.` : "Sans votre accord, votre bailleur vous remet les quittances autrement ; elles restent téléchargeables ici."}
        checked={!!d.e_receipts_consent}
        onChange={(v) => {
          if (busy) return;
          run("consent", () => put(meApi(leaseId, "/preferences"), { e_receipts_consent: v }), v ? "Accord enregistré." : "Accord retiré.");
        }}
      />
    </Card>
  );
}

export function TenantInsuranceSection({ leaseId }: { leaseId: string }) {
  const q = useGet<any>(meApi(leaseId));
  if (q.isLoading) return <Loading />;
  if (notTenant(q.error)) return null;
  if (q.error) return <ErrorBox error={q.error} />;
  const ins = q.data.insurance;
  return (
    <div className="stack">
      <Card title="Mon assurance habitation">
        <p>
          <CoverageStatus state={ins.coverage.state} label={ins.coverage.label} /> {ins.coverage.until ? <span className="small">jusqu’au {date(ins.coverage.until)}</span> : null}
        </p>
        <Notice>Votre bail peut vous demander d’assurer le logement contre les risques locatifs et d’en justifier chaque année : déposez ici l’attestation de votre assureur.</Notice>
        <ul className="gestion-list">
          {ins.certificates.map((c: any) => (
            <li key={c.id} className="gestion-line">
              <span>
                {c.insurer || "Attestation"} · {c.covers_from ? `du ${date(c.covers_from)} au ${date(c.covers_to)}` : ""}
                <span className="block small muted">{c.status === "validee" ? "Vérifiée par votre bailleur" : c.status === "a_corriger" ? `À corriger : ${c.review_note}` : "En attente de vérification"}</span>
              </span>
              <DocLink id={c.document_id}>Voir</DocLink>
            </li>
          ))}
        </ul>
      </Card>
      <InsuranceUpload orgId="" url={meApi(leaseId, "/insurance")} title="Déposer mon attestation" />
    </div>
  );
}
